/**
 * feat-299 — the wiring between the cards and Edit mode's EXISTING update
 * functions (spec §1, slice §4).
 *
 * Every save goes through one of `updateQuestion`, `updateReadingItem`,
 * `updateRehearsal`, `updateUnit` (= a Lesson, `/learn-lessons/{id}`) or
 * `updateLesson` (= a Drill, `/learn-drills/{id}`) — the naming is inverted,
 * see `quizTerminology.ts`. Each payload carries ONLY the edited key, so the
 * endpoints' partial-update semantics hold and no unrelated column is rewritten.
 * No new endpoint, and no approve/revert function anywhere (AT-14).
 *
 * After a successful write: patch the in-memory tree (no full refetch), then
 * re-read that Lesson's pending set (AT-12). The pending re-read is fire-and-
 * forget; the save is "done" once the write lands.
 *
 * `ReviewEditContext` is `null` outside the page, which is how the cards keep
 * rendering read-only, exactly as feat-298 shipped them, in isolation.
 */
import React, { createContext, useContext, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import {
  QuizContent,
  RehearsalCriterion,
  updateLesson,
  updateQuestion,
  updateReadingItem,
  updateRehearsal,
  updateUnit,
} from '../../../services/quizContentAdminService';
import {
  criteriaWithEdit,
  CriterionEdit,
  DrillPatch,
  patchDrill,
  patchQuestion,
  patchReading,
  patchRehearsal,
  patchUnit,
  QuestionPatch,
  ReadingPatch,
  UnitPatch,
} from './contentPatch';
import { ChipKind, itemChip, lessonChip, PendingApi, PendingState } from './pendingApprovals';
import { QUIZ_REVIEW_EDIT_COPY as COPY } from './editCopy';
import { APPROVAL_QUEUE_COPY } from '../curriculum/approvalCopy';
import { FOCUS } from './InlineEditable';

/** The Approval Queue route (`App.tsx`, `CurriculumApprovalQueue`). The queue
 *  has no per-Lesson deep link (design Q2). */
export const APPROVAL_QUEUE_PATH = '/quiz-approvals';

export interface ReviewEditApi {
  saveQuestion: (lessonId: string | null, id: string, patch: QuestionPatch) => Promise<void>;
  /**
   * feat-300 — a question write that ALREADY landed elsewhere (an AI-edit Apply).
   * Same tail as a save: patch the tree, then re-read that Lesson's pending set.
   * Never marks anything pending itself.
   */
  questionWritten: (lessonId: string | null, id: string, patch: QuestionPatch) => void;
  saveReading: (lessonId: string | null, id: string, patch: ReadingPatch) => Promise<void>;
  /** Name / scenario only. Criteria go through `saveCriterion`. */
  saveRehearsal: (
    lessonId: string | null,
    id: string,
    patch: { name?: string; scenario_text?: string },
  ) => Promise<void>;
  /**
   * One criterion field. Serialized per Rehearsal (QA-F1): see `useReviewEditApi`.
   * `loaded` is the card's render-time criteria — used ONLY when no criteria save
   * for this Rehearsal is queued or in flight, in which case it is the latest
   * confirmed state.
   */
  saveCriterion: (
    lessonId: string | null,
    id: string,
    loaded: RehearsalCriterion[],
    edit: CriterionEdit,
  ) => Promise<void>;
  saveUnit: (id: string, patch: UnitPatch) => Promise<void>;
  saveDrill: (lessonId: string | null, id: string, patch: DrillPatch) => Promise<void>;
  pending: PendingState;
}

export const ReviewEditContext = createContext<ReviewEditApi | null>(null);

/** The id of the Lesson (`QuizUnit`) whose subtree is rendering. */
export const LessonIdContext = createContext<string | null>(null);

export function useReviewEdit(): ReviewEditApi | null {
  return useContext(ReviewEditContext);
}

export function useLessonId(): string | null {
  return useContext(LessonIdContext);
}

/** One Rehearsal's criteria-save queue (QA-F1). */
interface CriteriaChain {
  /** Settles (never rejects) when the last queued save has finished. */
  tail: Promise<void>;
  /** The last criteria array the server accepted (or the loaded one). */
  confirmed: RehearsalCriterion[];
  /** Saves queued or in flight. The chain is dropped when it reaches 0. */
  depth: number;
}

export interface ReviewEditHooks {
  /** feat-300 — an inline save of a question succeeded (after the tree patch).
   *  The AI-edit layer re-reads its threads so an open proposal shows stale. */
  onQuestionSaved?: (id: string) => void;
}

export function useReviewEditApi(
  setContent: React.Dispatch<React.SetStateAction<QuizContent | null>>,
  pending: PendingApi,
  hooks: ReviewEditHooks = {},
): ReviewEditApi {
  const { state, refreshLesson } = pending;
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  /**
   * QA-F1: the criteria PUT is a whole-set replace, so two overlapping saves
   * built from the same render would overwrite each other. Saves for the SAME
   * Rehearsal therefore run one at a time, in commit order, and each builds its
   * payload at SEND time from `confirmed` — the last successful PUT's array —
   * with only its own edit applied. A failed save leaves `confirmed` untouched,
   * so later saves never carry its value, and only its own promise rejects (only
   * that field shows the error). The tree is patched with exactly what the
   * server accepted, in order, so the screen equals the last confirmed state.
   */
  const chainsRef = useRef(new Map<string, CriteriaChain>());
  const writers = useMemo(() => {
    const after = (lessonId: string | null, patch: (c: QuizContent) => QuizContent) => {
      setContent((prev) => (prev ? patch(prev) : prev));
      if (lessonId) refreshLesson(lessonId);
    };
    return {
      saveQuestion: async (lessonId: string | null, id: string, patch: QuestionPatch) => {
        await updateQuestion(id, patch);
        after(lessonId, (c) => patchQuestion(c, id, patch));
        hooksRef.current.onQuestionSaved?.(id);
      },
      questionWritten: (lessonId: string | null, id: string, patch: QuestionPatch) => {
        after(lessonId, (c) => patchQuestion(c, id, patch));
      },
      saveReading: async (lessonId: string | null, id: string, patch: ReadingPatch) => {
        await updateReadingItem(id, patch);
        after(lessonId, (c) => patchReading(c, id, patch));
      },
      saveRehearsal: async (
        lessonId: string | null,
        id: string,
        patch: { name?: string; scenario_text?: string },
      ) => {
        // Sends only its own key; `patchRehearsal` merges it, so `criteria` in
        // the tree is left alone (and a criteria patch leaves these alone).
        await updateRehearsal(id, patch);
        after(lessonId, (c) => patchRehearsal(c, id, patch));
      },
      saveCriterion: (
        lessonId: string | null,
        id: string,
        loaded: RehearsalCriterion[],
        edit: CriterionEdit,
      ): Promise<void> => {
        const chains = chainsRef.current;
        let chain = chains.get(id);
        if (!chain) {
          chain = { tail: Promise.resolve(), confirmed: loaded, depth: 0 };
          chains.set(id, chain);
        }
        const c = chain;
        const send = async () => {
          const criteria = criteriaWithEdit(c.confirmed, edit.index, edit.field, edit.value);
          await updateRehearsal(id, { criteria });
          c.confirmed = criteria;
          after(lessonId, (t) => patchRehearsal(t, id, { criteria }));
        };
        // Idle queue: send now (same tick as the commit). Otherwise wait for
        // every earlier save of this Rehearsal to settle first.
        const run = c.depth === 0 ? send() : c.tail.then(send);
        c.depth += 1;
        const settle = () => {
          c.depth -= 1;
          if (c.depth === 0 && chains.get(id) === c) chains.delete(id);
        };
        c.tail = run.then(settle, settle);
        return run;
      },
      saveUnit: async (id: string, patch: UnitPatch) => {
        await updateUnit(id, patch);
        after(id, (c) => patchUnit(c, id, patch));
      },
      saveDrill: async (lessonId: string | null, id: string, patch: DrillPatch) => {
        await updateLesson(id, patch);
        after(lessonId, (c) => patchDrill(c, id, patch));
      },
    };
  }, [setContent, refreshLesson]);
  return useMemo(() => ({ ...writers, pending: state }), [writers, state]);
}

// ------------------------------------------------------------ chips (NOTES §8)

const CHIP_GEOMETRY =
  'inline-flex items-center gap-1 align-middle ml-2 rounded-full px-2 py-[3px] ' +
  'text-[10px] font-bold leading-none not-italic whitespace-nowrap hover:underline';
const PENDING_CHIP = `${CHIP_GEOMETRY} bg-cream-dark ring-1 ring-black/5 text-inkMuted hover:text-ink ${FOCUS}`;
const NOTLIVE_CHIP = `${CHIP_GEOMETRY} bg-attention-surface text-attention-on ${FOCUS}`;

export const PendingChip: React.FC<{ kind: ChipKind; entity: string; id: string }> = ({
  kind,
  entity,
  id,
}) => {
  const notLive = kind === 'not-live';
  const sentence = notLive
    ? COPY.notLiveLabel
    : kind === 'pending-created'
      ? COPY.pendingLabelCreated
      : COPY.pendingLabelUpdated;
  return (
    <Link
      to={APPROVAL_QUEUE_PATH}
      className={notLive ? NOTLIVE_CHIP : PENDING_CHIP}
      title={sentence}
      aria-label={sentence}
      data-testid={`pending-chip-${entity}-${id}`}
    >
      <span aria-hidden>{notLive ? '⚠' : '●'}</span>{' '}
      {notLive ? APPROVAL_QUEUE_COPY.neverApprovedChip : COPY.pendingChip}
      <ArrowUpRight className="w-3 h-3" aria-hidden />
    </Link>
  );
};

/** The chip for an item/Drill in the current Lesson, or nothing. */
export const ItemPendingChip: React.FC<{ entity: string; id: string }> = ({ entity, id }) => {
  const api = useReviewEdit();
  const lessonId = useLessonId();
  if (!api) return null;
  const kind = itemChip(api.pending, lessonId, entity, id);
  return kind ? <PendingChip kind={kind} entity={entity} id={id} /> : null;
};

/** The Lesson banner's chip ("Not live" or pending), or nothing. */
export const LessonPendingChip: React.FC<{ id: string }> = ({ id }) => {
  const api = useReviewEdit();
  if (!api) return null;
  const kind = lessonChip(api.pending, id);
  return kind ? <PendingChip kind={kind} entity="learn_lesson" id={id} /> : null;
};
